import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Clock, Gauge, Hourglass, MinusCircle } from 'lucide-react';
import {
  MIN_MEASURED_POWER_COVERAGE_RATIO,
  MIN_MEASURED_POWER_WINDOW_DAYS,
  type UsePhaseAccuracyTier,
  type UsePhaseCustomerDetailResponse,
  type UsePhaseCustomerFigure,
  type UsePhasePueSource,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * Customer asset-detail "Operational emissions" section (SIL-347, T9 of
 * resources/plans/use-phase-carbon.md §Surfaces). Renders the Scope 2
 * (location-based) figure the customer endpoint (SIL-344) returns.
 *
 * Every render decision below traces to a locked plan rule — see the inline
 * comments — and every piece of required copy is rendered VERBATIM from the
 * shared constants on the response (`scopeLabel`, `noMarketBasedStatement`,
 * `boundsCaveat`, `annualRateLabel`, `screeningEstimateLine`) rather than
 * re-authored here, per the ticket's explicit instruction. `boundsCaveat`
 * (SIL-378) is server-selected between the two bounds-caveat literals on
 * `boundsBasis` — this component never chooses between them itself.
 *
 * NEVER placed on listings (plan decision 9 / "Do NOT" list) — this module
 * is only imported from the asset-detail v2 health tab.
 *
 * NEVER rendered adjacent to the embodied avoided-emissions claim
 * (`CarbonClaim.tsx` / listing carbon claim) in a way that invites summing —
 * this card is a visually distinct `Card` with its own heading, and no
 * shared helper here accepts both an avoided-emissions figure and this
 * use-phase figure (mirrors the DTO-level invariant in
 * `dto/use-phase.ts`'s `assetCarbonSummarySchema` docstring).
 */

// ---------------------------------------------------------------------------
// Local Card/CardLabel — duplicated (not imported) from AssetDetailV2.tsx to
// avoid a circular import between the two files; keep classNames in sync if
// that file's Card styling changes.
// ---------------------------------------------------------------------------

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-zinc-200 bg-white p-6">{children}</div>;
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{children}</div>
  );
}

// ---------------------------------------------------------------------------
// Tier badge — measured/partial/modeled/indicative. SIL-379 shipped the
// org-level PUE input surface, so `measured` IS reachable today (a real
// facility location + measured power + a supplied PUE) — this file's copy
// must never claim otherwise (SIL-378).
// ---------------------------------------------------------------------------

const TIER_LABEL: Record<UsePhaseAccuracyTier, string> = {
  measured: 'Measured',
  partial: 'Partial',
  modeled: 'Modeled',
  indicative: 'Indicative',
};

const TIER_BADGE_CLASS: Record<UsePhaseAccuracyTier, string> = {
  measured: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  partial: 'border-amber-300 bg-amber-50 text-amber-700',
  modeled: 'border-orange-300 bg-orange-50 text-orange-700',
  indicative: 'border-red-300 bg-red-50 text-red-700',
};

function TierBadge({ tier }: { tier: UsePhaseAccuracyTier }) {
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${TIER_BADGE_CLASS[tier]}`}
    >
      {TIER_LABEL[tier]}
    </span>
  );
}

/**
 * Lay-reader phrasing for `MIN_MEASURED_POWER_WINDOW_DAYS` — rendered from
 * the constant rather than hardcoded as "a full month," so the copy cannot
 * silently drift from the actual gate if that constant ever moves (see its
 * own docstring — "reasoned, not calibrated, will move").
 */
function formatMinWindowForLayReader(days: number): string {
  if (days > 0 && days % 30 === 0) {
    const months = days / 30;
    return months === 1 ? 'a full month' : `${months} months`;
  }
  return `${days} days`;
}

/** Lay-reader percentage for `MIN_MEASURED_POWER_COVERAGE_RATIO` — e.g. `0.8` -> `"80%"`. */
const MIN_COVERAGE_PCT_LABEL = `${Math.round(MIN_MEASURED_POWER_COVERAGE_RATIO * 100)}%`;
const MIN_WINDOW_LABEL = formatMinWindowForLayReader(MIN_MEASURED_POWER_WINDOW_DAYS);

type TierCopy = { reason: string; remedy: string | null };

/**
 * Plain-language reason + remedy for a COMPUTED figure (`not_computed_*` is
 * handled separately below). Branches on BOTH `reportingEligibility` AND
 * `powerBasis` (SIL-378) — `deriveReportingEligibility` maps BOTH a
 * genuinely-telemetry-free asset (`powerBasis === 'archetype_default'`) AND
 * an asset with real but under-qualified telemetry (`powerBasis ===
 * 'telemetry_measured'` failing the `isMeasuredPowerQualified` window/
 * coverage gate) onto the SAME `screening_estimate_default_power{,_and_
 * location}` eligibility values. Rendering one "no telemetry" string for
 * both is Defect A — a real production asset with 21/30 days of measured
 * 238W telemetry was told it had none. `reportingEligibility` alone cannot
 * tell these apart; `powerBasis` on the customer figure (SIL-378) can.
 *
 * The `reportable` / `screening_estimate_default_location` /
 * `screening_estimate_default_pue` branches don't depend on `powerBasis` —
 * power is unconditionally measured-and-qualified on all three (that's
 * what got them past `screening_estimate_default_power{,_and_location}` in
 * the first place) — so they stay single strings.
 */
function getTierCopy(figure: Extract<UsePhaseCustomerFigure, { status: 'computed' }>): TierCopy {
  const { reportingEligibility, powerBasis, daysWithData, calendarDaysInWindow } = figure;

  switch (reportingEligibility) {
    case 'reportable':
      return {
        reason:
          "Power draw is measured from this asset's own telemetry, using a real recorded facility location and a supplied PUE value.",
        remedy: null,
      };
    case 'screening_estimate_default_location':
      return {
        reason:
          "Power draw is measured from this asset's telemetry, but no facility location is recorded for it, so a world-average grid-intensity factor is used instead of your actual location.",
        remedy:
          "Add this asset's data-center location to replace the default with your facility's actual grid intensity.",
      };
    case 'screening_estimate_default_pue':
      return {
        reason:
          "Power draw and facility location are both known, but no facility-specific PUE is recorded, so an industry-average PUE is applied instead.",
        remedy:
          "An org admin can supply your facility's PUE from the Profile page to replace the industry default.",
      };
    case 'screening_estimate_default_power':
      return powerBasis === 'telemetry_measured'
        ? {
            reason:
              `Power draw comes from this asset's own telemetry and was used to compute this figure. ` +
              `Coverage for this period is ${daysWithData} of ${calendarDaysInWindow} days, below the ` +
              `${MIN_COVERAGE_PCT_LABEL} of days we require before grading a figure as measured, so it ` +
              `is graded as modeled instead.`,
            remedy:
              `Raising daily telemetry coverage above ${MIN_COVERAGE_PCT_LABEL} across the period will ` +
              `improve this asset's grade.`,
          }
        : {
            // SIL-376 — all 19 archetype-path production assets at time of
            // writing resolve to the SAME 581W across 10 distinct SKUs
            // because the config sent to BoaviztAPI is empty. The "not a
            // profile matched to this asset's configuration" clause below
            // is load-bearing and must not be softened until that's fixed.
            reason:
              "This asset has no power telemetry for this period, so power draw is modeled from " +
              "BoaviztAPI's generic server archetype - one default server profile, not a profile " +
              "matched to this asset's configuration.",
            remedy:
              `Connecting telemetry replaces the assumption with this asset's own measured draw. Power ` +
              `counts as measured once telemetry covers at least ${MIN_COVERAGE_PCT_LABEL} of days over ${MIN_WINDOW_LABEL}.`,
          };
    case 'screening_estimate_default_power_and_location':
      return powerBasis === 'telemetry_measured'
        ? {
            reason:
              `Power draw comes from this asset's own telemetry and was used to compute this figure, but ` +
              `coverage for this period is ${daysWithData} of ${calendarDaysInWindow} days, below the ` +
              `${MIN_COVERAGE_PCT_LABEL} of days we require before grading a figure as measured. No facility ` +
              `location is recorded either, so a world-average grid-intensity factor is used instead of ` +
              `your own grid.`,
            remedy:
              `Raising daily telemetry coverage above ${MIN_COVERAGE_PCT_LABEL} and recording this asset's ` +
              `data-center location will improve this asset's grade.`,
          }
        : {
            // SIL-376 — same empty-BoaviztAPI-config caveat as the
            // power-only archetype branch above.
            reason:
              "This asset has no power telemetry for this period, so power draw is modeled from " +
              "BoaviztAPI's generic server archetype - one default server profile, not a profile " +
              "matched to this asset's configuration. No facility location is recorded either, so a " +
              "world-average grid-intensity factor is used instead of your own grid.",
            remedy:
              "Connecting telemetry and recording this asset's data-center location will sharpen the estimate.",
          };
  }
}

const PUE_SOURCE_LABEL: Record<UsePhasePueSource, string> = {
  customer_dc: 'supplied by your facility',
  org: 'supplied by your organization',
  industry_default: 'industry default',
};

const fmtKg = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/**
 * The national-grid-factor limitation — disclosed at the point of use rather
 * than deferred to a methodology page (plan: "ship-blocking for the CSV, and
 * the same logic applies here"). This point-of-use disclosure stays even now
 * that SIL-352 restored the methodology link below: the full methodology
 * page is a click away, but the summary line here still needs to be readable
 * without following it. Rendered as a collapsed `<details>` so it's visible
 * without a click-through, but doesn't dominate the section.
 */
function GridFactorDisclosure() {
  return (
    <details className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
      <summary className="cursor-pointer font-medium text-zinc-700">
        About the grid-intensity factor
      </summary>
      <p className="mt-1.5">
        Grid-intensity factors are national annual averages. Sub-national variation — for example,
        U.S. eGRID subregions differ by roughly 2&ndash;3&times; — is not modeled. The GHG Protocol
        Scope 2 Guidance recommends using sub-national factors where available; this figure does
        not yet.
      </p>
    </details>
  );
}

/**
 * The computed-figure body. Every required element from the ticket's
 * "Required content" list is present: /yr-unmissable value, range always
 * beside it (never the min alone), the bounds caveat verbatim, tier badge +
 * reason + remedy, inline PUE with source, the no-market-based statement, the
 * grid-factor disclosure, and a methodology link.
 *
 * The methodology link the ticket originally called for was removed: it
 * pointed at the internal Cloudflare Pages wiki, which is not public, so it
 * 404'd for every customer. SIL-352 restored it against the in-app
 * `/carbon-methodology` route (login-gated customer-portal page, not the old
 * externally-gated wiki).
 */
function ComputedUsePhaseBody({ response }: { response: UsePhaseCustomerDetailResponse }) {
  const figure = response.figure;
  if (figure.status !== 'computed') return null;

  const tierCopy = getTierCopy(figure);

  return (
    <>
      <div className="mt-3.5 flex flex-wrap items-end gap-x-3 gap-y-1">
        <div className="flex items-baseline gap-1.5">
          <Gauge className="mb-0.5 size-4 flex-none text-zinc-400" aria-hidden />
          <span className="font-mono text-2xl font-semibold text-zinc-900">
            {fmtKg(figure.gwpUseAnnualKgco2eq as unknown as number)}
          </span>
          <span className="text-sm text-zinc-500">
            kgCO&#8322;e<span className="font-bold text-zinc-700">/yr</span>
          </span>
        </div>
        <TierBadge tier={figure.accuracyTier} />
      </div>

      {/* SIL-378 — always-rendered screening-estimate line, adjacent to the
          value/tier badge above (not buried inside the eligibility-specific
          reason string below, which was the weakest surface for it). Every
          COMPUTED figure renders this, regardless of eligibility. */}
      <p className="mt-1 text-xs font-medium text-zinc-500">{response.screeningEstimateLine}</p>

      {/* Range beside the central value — never the minimum alone (GHG
          Protocol Appendix B, p158-159: the reported scenario must be
          representative, "not the scenario with the lowest emissions"). */}
      <div className="mt-1 font-mono text-[13px] text-zinc-500">
        Range: {fmtKg(figure.gwpUseAnnualMinKgco2eq as unknown as number)}&ndash;
        {fmtKg(figure.gwpUseAnnualMaxKgco2eq as unknown as number)} kgCO&#8322;e/yr
      </div>

      {/* Bounds caveat — verbatim, server-selected (SIL-378) between
          `USE_PHASE_BOUNDS_CAVEAT` (observed variation, `boundsBasis ===
          'observed_p10_p90'`) and `USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE`
          (BoaviztAPI's own archetype min/max, zero observation,
          `boundsBasis === 'archetype_span'`) — never re-authored here and
          never labeled a confidence/uncertainty interval. */}
      <p className="mt-2 text-xs text-zinc-500">{response.boundsCaveat}</p>

      <p className="mt-3 text-sm text-zinc-700">
        {tierCopy.reason}
        {tierCopy.remedy && <> {tierCopy.remedy}</>}
      </p>

      {/* SIL-388/389 — accelerator-power assumption, only present when
          accelerator draw was actually folded into this figure. Names both
          the population (slot capacity, an upper bound) and workload (50%
          of TDP) assumptions in one sentence, verbatim from the shared
          builder — never re-authored here. */}
      {figure.acceleratorAssumption && (
        <p className="mt-2 text-xs text-zinc-500">{figure.acceleratorAssumption}</p>
      )}

      {/* PUE inline, with its source — not in a tooltip. Scope 2 is purchased
          electricity, which includes cooling; the multiplication must stay
          visible. */}
      <p className="mt-2 text-sm text-zinc-600">
        PUE used: <span className="font-mono font-semibold text-zinc-800">{figure.pue}</span>{' '}
        ({PUE_SOURCE_LABEL[figure.pueSource]})
      </p>

      {/* Verbatim `USE_PHASE_NO_MARKET_BASED_STATEMENT`. */}
      <p className="mt-2 text-xs text-zinc-500">{response.noMarketBasedStatement}</p>

      <GridFactorDisclosure />

      <p className="mt-3 text-xs">
        <Link to="/carbon-methodology" className="font-medium text-zinc-600 underline hover:text-zinc-800">
          Methodology
        </Link>
      </p>
    </>
  );
}

/**
 * `not_computed` body — each reason renders visibly distinct from the
 * others (plan: a newly-onboarded asset must not read as broken software,
 * and neither `pending_first_computation` nor
 * `no_archetype_without_telemetry` may read as "Not applicable" — only
 * `unsupported_asset_class` is that). Different icon, different color,
 * different copy voice per reason.
 */
function NotComputedUsePhaseBody({ figure }: { figure: Extract<UsePhaseCustomerFigure, { status: 'not_computed' }> }) {
  if (figure.reason === 'pending_first_computation') {
    return (
      <div className="mt-3.5 flex items-start gap-2.5">
        <Hourglass className="mt-0.5 size-4 flex-none text-blue-500" aria-hidden />
        <div className="text-sm text-zinc-600">
          <span className="font-semibold text-blue-700">Pending first computation.</span>{' '}
          This asset is newly onboarded and hasn&apos;t had its first monthly operational-emissions
          computation yet. Check back after the next monthly run.
        </div>
      </div>
    );
  }
  if (figure.reason === 'no_archetype_without_telemetry') {
    return (
      <div className="mt-3.5 flex items-start gap-2.5">
        <Clock className="mt-0.5 size-4 flex-none text-amber-500" aria-hidden />
        <div className="text-sm text-zinc-600">
          <span className="font-semibold text-amber-700">Awaiting telemetry.</span>{' '}
          This asset&apos;s hardware type has no default operational-emissions estimate without
          real power telemetry - it is not a server chassis we can model a typical power draw for.
          Once telemetry is connected for this asset, an operational-emissions figure will be
          computed from its actual measured power draw.
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3.5 flex items-start gap-2.5">
      <MinusCircle className="mt-0.5 size-4 flex-none text-zinc-300" aria-hidden />
      <div className="text-sm text-zinc-400">
        <span className="font-semibold">Not applicable.</span> Operational emissions aren&apos;t
        calculated for this asset&apos;s hardware class.
      </div>
    </div>
  );
}

export function UsePhaseDetail({ response }: { response: UsePhaseCustomerDetailResponse }) {
  return (
    <Card>
      {/* The parenthetical is not optional — CSRD/ESRS E1-6 expects both
          location- and market-based Scope 2, and dropping the qualifier lets
          a customer drop this number into a market-based field. */}
      <CardLabel>Operational emissions — GHG Protocol {response.scopeLabel}</CardLabel>
      {response.figure.status === 'computed' ? (
        <ComputedUsePhaseBody response={response} />
      ) : (
        <NotComputedUsePhaseBody figure={response.figure} />
      )}
    </Card>
  );
}

interface ApiError extends Error {
  status?: number;
}

/**
 * Fetching container — separate from `UsePhaseDetail` so component tests can
 * render the display logic directly with a hand-built response, without
 * mocking react-query/the API client.
 */
export function UsePhaseCard({ assetId }: { assetId: string }) {
  const { data } = useQuery<UsePhaseCustomerDetailResponse, ApiError>({
    queryKey: qk.assets.usePhase(assetId),
    queryFn: async () => {
      const res = await api.api.client.assets[':assetId']['use-phase'].$get({
        param: { assetId },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as UsePhaseCustomerDetailResponse;
    },
  });

  if (!data) return null;
  return <UsePhaseDetail response={data} />;
}
